import { cn } from "@repo/lib";
import type { ButtonHTMLAttributes, ReactNode } from "react";
import { Button } from "@/components/button";

interface SectionHeaderProps {
  className?: string;
  children: JSX.Element | JSX.Element[];
}

interface SectionHeaderIconProps {
  className?: string;
  children: ReactNode;
}

interface SectionHeaderTitleProps {
  children: string;
  className?: string;
}

interface SectionHeaderDescriptionProps {
  children: string;
  className?: string;
}

interface SectionHeaderButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  children: ReactNode;
  onClick?: () => void;
}

export const SectionHeaderRoot = ({
  className,
  children,
}: SectionHeaderProps): JSX.Element => {
  return (
    <div className={cn("flex flex-row items-center justify-between w-full", className)}>
      {children}
    </div>
  );
};

export const SectionHeaderContent = ({
  className,
  children,
}: SectionHeaderProps): JSX.Element => {
  return (
    <div className={cn("flex flex-row gap-2 items-center", className)}>{children}</div>
  );
};

export const SectionHeaderIcon = ({
  className,
  children,
}: SectionHeaderIconProps): JSX.Element => {
  return (
    <div
      className={cn(
        "bg-secondary-50 rounded-full flex items-center justify-center p-2",
        className,
      )}
    >
      {children}
    </div>
  );
};

export const SectionHeaderInfo = ({
  className,
  children,
}: SectionHeaderProps): JSX.Element => {
  return (
    <div className={cn("flex flex-col leading-none gap-1", className)}>{children}</div>
  );
};

export const SectionHeaderTitle = ({
  children,
  className,
}: SectionHeaderTitleProps): JSX.Element => {
  return (
    <h3 className={cn("text-neutral-900 font-semibold text-lg", className)}>
      {children}
    </h3>
  );
};

export const SectionHeaderDescription = ({
  children,
  className,
}: SectionHeaderDescriptionProps): JSX.Element => {
  return <p className={cn("text-neutral-900 text-xs", className)}>{children}</p>;
};

export const SectionHeaderButton = ({
  children,
  onClick,
}: SectionHeaderButtonProps): JSX.Element => {
  return (
    <Button
      className="pl-2 pr-0 min-w-0 min-h-0"
      type="button"
      rounded
      size="small"
      onClick={onClick}
    >
      <div className="text-xs px-1 m-0 flex flex-row items-center justify-center">
        {children}
      </div>
    </Button>
  );
};
